import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'

import { FirebaseError } from 'firebase/app'
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  increment,
  orderBy,
  query,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore'

import { auth, firestore } from '../firebase/firebaseConfig'
import { subscribeToCollection } from '../firebase/utils/collectionActions'
import { updateDocument } from '../firebase/utils/documentsActions'
import { NewLog, Student } from '../types'
import { handleErrors } from '../utilFunctions/firebaseErrors'

// import { guardedCall } from './requestGuard'

// export const fetchStudent = (id: string) =>
//   guardedCall(async () => {
//     const ref = doc(firestore, 'students', id)
//     const snap = await getDoc(ref)
//     if (!snap.exists()) throw new Error('not-found')
//     return snap.data()
//   })

interface StudentContextProps {
  studentsList: Student[] | null
  activeStudent: Student | null
  studentLogs: NewLog[] | null
  studentError: string | null
  onLogCreation: boolean

  setActiveStudent: Dispatch<SetStateAction<Student | null>>
  setStudentsList: Dispatch<SetStateAction<Student[] | null>>
  createStudent: (newStudent: Student) => Promise<void>
  saveStudent: (updatedStudent: Student, studentId: string) => Promise<void>
  // getStudentLogs: (studentId: string) => Promise<DocumentData[] | null>
  uploadLog: (
    studentId: string,
    logData: NewLog,
    cuponId: string | null,
  ) => Promise<{
    message: string
    error: null | boolean
  }>
  deleteExerciseByURLFromLog: (
    studentId: string,
    logUID: string,
    exerciseURL: string,
  ) => Promise<{ error: boolean | null; message: string }>
  deleteLog: (
    studentId: string,
    logUID: string,
    professorId: string,
  ) => Promise<{ error: boolean | null; message: string }>
  saveFormAnswers: (
    studentId: string,
    formData: Record<string, string>,
  ) => Promise<string>
  setOnLogCreation: Dispatch<SetStateAction<boolean>>
}

const StudentContext = createContext<StudentContextProps>({
  studentsList: null,
  activeStudent: null,
  studentLogs: null,
  studentError: null,
  onLogCreation: false,

  setActiveStudent: () => Promise.resolve(null),
  setStudentsList: () => Promise.resolve(null),
  createStudent: () => Promise.resolve(),
  saveStudent: () => Promise.resolve(),
  uploadLog: () => Promise.resolve({ message: '', error: null }),
  deleteExerciseByURLFromLog: () =>
    Promise.resolve({ message: '', error: null }),
  deleteLog: () => Promise.resolve({ message: '', error: null }),
  saveFormAnswers: () => Promise.resolve(''),
  setOnLogCreation: () => Promise.resolve(false),
})

export const useStudents = () => useContext(StudentContext)
// * Arquitectura y separación de responsabilidades - punto 1
// const StudentsContext = createContext<ReturnType<typeof useStudents> | null>(
//   null,
// )

// export const StudentsProvider = ({
//   children,
// }: {
//   children: React.ReactNode
// }) => {
//   const value = useStudents()
//   return (
//     <StudentsContext.Provider value={value}>
//       {children}
//     </StudentsContext.Provider>
//   )
// }

export const StudentsProvider = ({ children }: { children: ReactNode }) => {
  const [activeStudent, setActiveStudent] = useState<Student | null>(null)
  const [studentsList, setStudentsList] = useState<Student[] | null>(null)
  const [studentLogs, setStudentLogs] = useState<NewLog[] | null>(null)
  const [studentError, setStudentError] = useState<string | null>(null)
  const [onLogCreation, setOnLogCreation] = useState<boolean>(false)
  // TODO: add loading
  // const [studentsLoading, setStudentsLoading] = useState(true)

  useEffect(() => {
    let unsubscribeStudents: (() => void) | undefined

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const studentsRef = collection(firestore, 'students')
        unsubscribeStudents = subscribeToCollection<Student>(
          studentsRef,
          setStudentsList,
          // setStudentsLoading,
          // setStudentError,
        )

        // return () => unsubscribeStudents()
      }
      // TODO: Uncomment lines below to handle no user case
      // else {
      //   setStudentsList(null)
      //   setStudentsLoading(false)
      // }
    })

    return () => {
      unsubscribeStudents?.()
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (activeStudent) {
      if (studentsList) {
        const _updatedStudent =
          studentsList.find((_stu) => _stu.uid === activeStudent.uid) || null

        setActiveStudent(_updatedStudent)
      }

      subscribeToLogsOrderedByDate(activeStudent.uid)
    } else {
      setStudentLogs(null)
    }
  }, [activeStudent, studentsList])

  const createStudent = async (newStudent: Student) => {
    const newStudentRef = doc(collection(firestore, 'students'))

    await setDoc(newStudentRef, { ...newStudent, uid: newStudentRef.id })
  }

  const saveStudent = async (updatedStudent: Student, studentId: string) => {
    const documentRef = doc(firestore, 'students', studentId)

    await updateDocument<Student>(documentRef, updatedStudent)
  }

  const saveFormAnswers = async (
    studentId: string,
    formData: Record<string, string>,
  ) => {
    try {
      const docRef = doc(firestore, 'students', studentId)

      await updateDoc(docRef, {
        form: formData,
      })

      localStorage.setItem('hasForm', 'true')
      return 'Formulario guardado con éxito'
    } catch (error) {
      const { code } = error as FirebaseError
      return handleErrors(code)
    }
  }

  const subscribeToLogsOrderedByDate = (studentId: string) => {
    const logsCollectionRef = collection(
      firestore,
      'students',
      studentId,
      'logs',
    )
    // console.log('llame a la subcol', studentId)
    const q = query(logsCollectionRef, orderBy('date', 'asc'))

    const unsubscribeLogs = subscribeToCollection<NewLog>(
      q,
      setStudentLogs,
      setStudentError,
    )

    return () => unsubscribeLogs()
  }

  const uploadLog = async (
    studentId: string,
    logData: NewLog,
    cuponId: string | null,
  ) => {
    const profRef = doc(firestore, 'professors', logData.professorUID)
    try {
      const studentRef = doc(firestore, 'students', studentId)

      const logsCollectionRef = collection(studentRef, 'logs')
      const logDocRef = doc(logsCollectionRef, logData.uid)
      const logDoc = await getDoc(logDocRef)

      const batch = writeBatch(firestore)

      if (logDoc.exists()) {
        const profSnap = await getDoc(profRef)

        if (!profSnap.exists()) {
          return {
            message: 'Hubo un error por favor intentalo de nuevo profesor',
            error: true,
          }
        }
        batch.update(logDocRef, logData)
        const { logs } = profSnap.data()

        const updatedItems = logs.map(
          (item: { date: string; logUID: string; studentUID: string }) =>
            item.logUID === logData.uid
              ? { ...item, date: logData.date }
              : item,
        )

        batch.update(profRef, { logs: updatedItems })
      } else {
        batch.set(logDocRef, {
          ...logData,
          uid: logDocRef.id,
          cuponId: cuponId,
        })

        batch.update(profRef, {
          logs: arrayUnion({
            studentUID: studentId,
            logUID: logDocRef.id,
            date: logData.date,
          }),
        })

        const studentSnap = await getDoc(studentRef)
        if (!studentSnap.exists()) {
          return {
            message: 'Hubo un error por favor intentalo de nuevo student',
            error: true,
          }
        }

        const { hasActiveBonus, activeBonus } = studentSnap.data()

        batch.update(studentRef, { currentAttendance: increment(1) })

        if (hasActiveBonus) {
          const cuponRef = doc(firestore, 'cupons', activeBonus)
          const cuponSnap = await getDoc(cuponRef)

          if (cuponSnap.exists()) {
            batch.update(cuponRef, {
              attendance: increment(1),
            })
          }
        }
      }

      await batch.commit()
      return {
        message: `Bitacora ${logDoc.exists() ? `actualizada` : `creada`} con exito`,
        error: null,
      }
    } catch (error) {
      console.error('Error al subir la bitacora:', error)
      return {
        message: 'Hubo un error por favor intentalo de nuevo gral',
        error: true,
      }
    }
  }

  const deleteExerciseByURLFromLog = async (
    studentId: string,
    logUID: string,
    exerciseURL: string,
  ): Promise<{ error: boolean | null; message: string }> => {
    const logDocRef = doc(firestore, 'students', studentId, 'logs', logUID)

    let visibilityFlag = false
    try {
      const logSnapshot = await getDoc(logDocRef)

      if (!logSnapshot.exists()) {
        return {
          message: 'Ejercicio no encontrado',
          error: true,
        }
      }

      const logData = logSnapshot.data() as NewLog
      const updatedExercises = logData.exercises.map((exercise) => {
        if (exercise.exercise.url === exerciseURL) {
          visibilityFlag = !exercise.visibility
          return { ...exercise, visibility: !exercise.visibility || false } // Agregar el flag
        }
        return exercise // Mantener los ejercicios no modificados
      })

      // Actualizar el documento con el array modificado
      await updateDoc(logDocRef, { exercises: updatedExercises })

      return {
        message: `Ejercicio ${!visibilityFlag ? 'eliminado' : 'agregado'} con exito ${!visibilityFlag ? 'de' : 'a'} la lista`,
        error: null,
      }
    } catch (error) {
      console.error('Error al eliminar el ejercicio de la lista:', error)
      return {
        message: `Hubo un error al ${!visibilityFlag ? 'eliminar' : 'agregar'} el ejercicio de la lista.`,
        error: true,
      }
    }
  }

  const deleteLog = async (
    studentId: string,
    logUID: string,
    professorId: string,
  ) => {
    const studentRef = doc(firestore, 'students', studentId)
    const profRef = doc(firestore, 'professors', professorId)

    const logsCollectionRef = collection(studentRef, 'logs')
    const logDocRef = doc(logsCollectionRef, logUID)

    const batch = writeBatch(firestore)

    try {
      batch.delete(logDocRef)

      const profDoc = await getDoc(profRef)

      if (!profDoc.exists()) {
        return {
          message: 'Hubo un error por favor intentalo de nuevo prof del',
          error: true,
        }
      }

      const logs = Array.isArray(profDoc.data().logs) ? profDoc.data().logs : []
      const filteredLogs = logs.filter(
        (log: { date: string; logUID: string; studentUID: string }) =>
          log.logUID !== logUID,
      )

      batch.update(profRef, {
        logs: filteredLogs,
      })

      const studentSnap = await getDoc(studentRef)
      if (!studentSnap.exists()) {
        return {
          message: 'Hubo un error por favor intentalo de nuevo student del',
          error: true,
        }
      }

      const { hasActiveBonus, activeBonus, currentAttendance } =
        studentSnap.data()

      if (typeof currentAttendance === 'number' && currentAttendance > 0) {
        batch.update(studentRef, { currentAttendance: increment(-1) })
      }

      if (hasActiveBonus) {
        const cuponRef = doc(firestore, 'cupons', activeBonus)
        const cuponSnap = await getDoc(cuponRef)

        if (
          cuponSnap.exists() &&
          typeof cuponSnap.data().attendance === 'number' &&
          cuponSnap.data().attendance > 0
        ) {
          batch.update(cuponRef, {
            attendance: increment(-1),
          })
        }
      }

      await batch.commit()
      return {
        message: `Bitacora eliminada con exito`,
        error: null,
      }
    } catch (error) {
      return {
        message: 'Hubo un error por favor intentalo de nuevo gral del',
        error: true,
      }
    }
  }

  return (
    <StudentContext.Provider
      value={{
        studentsList,
        activeStudent,
        studentLogs,
        studentError,
        onLogCreation,
        setActiveStudent,
        setStudentsList,
        createStudent,
        saveStudent,
        uploadLog,
        deleteExerciseByURLFromLog,
        deleteLog,
        saveFormAnswers,
        setOnLogCreation,
        // studentsLoading,
        // studentError,
      }}
    >
      {children}
    </StudentContext.Provider>
  )
}
